import { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import {
    ActivityPeriod,
    Center,
    CenterAppointment,
    CenterAppointmentPage,
    CenterPaymentReport,
    CenterConsentResult,
    CenterInvitation,
    CenterInvitationInput,
    CenterInvitationPreview,
    CenterSeats,
    CenterMember,
    CenterMembershipView,
    CenterPaymentSettings,
    CenterPaymentSettingsInput,
    CenterPatient,
    CenterPatientPage,
    CenterUnits,
    CenterPaymentPage,
    CenterRegistrationInput,
    CenterRegistrationResponse,
    CenterToolPolicy,
    CenterUpdateInput,
    MemberCaseload,
    MemberHandoverInput,
    PatientReassignment,
    ReassignmentPreview,
    ReassignmentPreviewInput,
    ReportGranularity,
    TherapistAgendaReport,
    TherapistSessionReport,
    ToolPolicyState,
    CenterRole,
    CenterCourse,
    CenterPatientDetail,
    CourseBatchResult,
    CourseInput,
    InvitationBatchResult,
    StudentBatchResult,
    StudentInput,
    StudentUpdate,
} from "../../domain/Center";
import type {
    PatientCustomField,
    PatientCustomFieldInput,
    PatientCustomFieldUpdate,
    PatientCustomValues,
    PatientCustomValuesInput,
} from "../../domain/PatientCustomField";

export const CenterAPI = {
    /**
     * Sign up a centre. Its own endpoint, not a mode of therapist sign-up:
     * the organisation is the first thing that exists here.
     */
    registerCenter: (
        payload: CenterRegistrationInput
    ): Promise<AxiosResponse<CenterRegistrationResponse>> =>
        apiClient.post("/auth/register-center", payload),

    /**
     * The caller's centre. A 404 means they have none, which is the ordinary
     * answer for a therapist who works alone — callers treat it as "no centre",
     * never as a failure.
     */
    me: (): Promise<AxiosResponse<CenterMembershipView>> => apiClient.get("/centers/me"),

    update: (id: string, payload: CenterUpdateInput): Promise<AxiosResponse<Center>> =>
        apiClient.patch(`/centers/${id}`, payload),

    /** Replace the logo. The server re-encodes it before anybody can load it. */
    uploadLogo: (id: string, file: File): Promise<AxiosResponse<Center>> => {
        const form = new FormData();
        form.append("file", file);
        return apiClient.put(`/centers/${id}/logo`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    removeLogo: (id: string): Promise<AxiosResponse<Center>> =>
        apiClient.delete(`/centers/${id}/logo`),

    /**
     * The roster. `practisingOnly` is the list any therapist picker or
     * assignment target must use: it leaves out administrators who do not
     * treat, and members who are suspended.
     */
    members: (
        id: string,
        options: { practisingOnly?: boolean } = {}
    ): Promise<AxiosResponse<CenterMember[]>> =>
        apiClient.get(`/centers/${id}/therapists`, {
            params: options.practisingOnly ? { practising_only: true } : undefined,
        }),

    /** What a member treats in the centre: individuals, couples and groups apart. */
    memberCaseload: (id: string, membershipId: string): Promise<AxiosResponse<MemberCaseload>> =>
        apiClient.get(`/centers/${id}/members/${membershipId}/caseload`),

    /**
     * Hand over everything the member treats here, then take them out of the
     * centre. The server checks the whole plan before moving anybody.
     */
    handOverAndRemove: (
        id: string,
        membershipId: string,
        payload: MemberHandoverInput
    ): Promise<AxiosResponse<void>> =>
        apiClient.post(`/centers/${id}/members/${membershipId}/hand-over-and-remove`, payload),

    /** Change what somebody does in the centre. They stay in it. */
    changeMemberRoles: (
        id: string,
        membershipId: string,
        roles: CenterRole[]
    ): Promise<AxiosResponse<CenterMember>> =>
        apiClient.patch(`/centers/${id}/members/${membershipId}`, { roles }),

    /**
     * Hand over what somebody treats, then change their roles — for a
     * therapist who stops treating but stays. Checked in full before anything
     * moves, like a departure.
     */
    handOverAndChangeRoles: (
        id: string,
        membershipId: string,
        payload: MemberHandoverInput & { roles: CenterRole[] }
    ): Promise<AxiosResponse<CenterMember>> =>
        apiClient.post(
            `/centers/${id}/members/${membershipId}/hand-over-and-change-roles`,
            payload
        ),
};

export const CenterInvitationAPI = {
    list: (centerId: string): Promise<AxiosResponse<CenterInvitation[]>> =>
        apiClient.get(`/centers/${centerId}/invitations`),

    invite: (
        centerId: string,
        payload: CenterInvitationInput
    ): Promise<AxiosResponse<CenterInvitation>> =>
        apiClient.post(`/centers/${centerId}/invitations`, payload),

    /** Many addresses at once; answered per address, in order. */
    inviteMany: (
        centerId: string,
        payload: { emails: string[]; language: "es" | "en" }
    ): Promise<AxiosResponse<{ results: InvitationBatchResult[] }>> =>
        apiClient.post(`/centers/${centerId}/invitations/batch`, payload),

    seats: (centerId: string): Promise<AxiosResponse<CenterSeats>> =>
        apiClient.get(`/centers/${centerId}/seats`),

    /** Stops an unused invitation. Removing a member is a different act. */
    revoke: (centerId: string, invitationId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/invitations/${invitationId}`),

    /** Unauthenticated: the person opening the link may have no account yet. */
    preview: (token: string): Promise<AxiosResponse<CenterInvitationPreview>> =>
        apiClient.get(`/center-invitations/${token}`),

    accept: (token: string): Promise<AxiosResponse<CenterMembershipView>> =>
        apiClient.post(`/center-invitations/${token}/accept`),
};

export const CenterDirectoryAPI = {
    /** One patient the centre holds, opened from the directory. */
    patient: (centerId: string, patientId: string): Promise<AxiosResponse<CenterPatientDetail>> =>
        apiClient.get(`/centers/${centerId}/patients/${patientId}`),

    /** A training centre groups students it already has under one mentor. */
    createGroup: (
        centerId: string,
        input: { therapist_id: string; patient_ids: string[]; display_name: string | null }
    ): Promise<AxiosResponse<{ group_id: string }>> => apiClient.post(`/centers/${centerId}/groups`, input),

    /** A centre makes a couple of two patients it already has, of one therapist. */
    createCouple: (
        centerId: string,
        input: { therapist_id: string; patient_ids: string[]; display_name: string | null }
    ): Promise<AxiosResponse<{ couple_id: string }>> => apiClient.post(`/centers/${centerId}/couples`, input),

    renameCouple: (centerId: string, coupleId: string, displayName: string | null): Promise<AxiosResponse<void>> =>
        apiClient.patch(`/centers/${centerId}/couples/${coupleId}`, { display_name: displayName }),

    replaceCouplePartner: (
        centerId: string,
        coupleId: string,
        oldPatientId: string,
        newPatientId: string
    ): Promise<AxiosResponse<void>> =>
        apiClient.put(`/centers/${centerId}/couples/${coupleId}/members/${oldPatientId}`, { patient_id: newPatientId }),

    renameGroup: (centerId: string, groupId: string, displayName: string | null): Promise<AxiosResponse<void>> =>
        apiClient.patch(`/centers/${centerId}/groups/${groupId}`, { display_name: displayName }),

    addGroupMember: (centerId: string, groupId: string, patientId: string): Promise<AxiosResponse<void>> =>
        apiClient.post(`/centers/${centerId}/groups/${groupId}/members`, { patient_id: patientId }),

    removeGroupMember: (centerId: string, groupId: string, patientId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/groups/${groupId}/members/${patientId}`),

    /** A training centre corrects one of its students. */
    updateStudent: (
        centerId: string,
        patientId: string,
        changes: StudentUpdate
    ): Promise<AxiosResponse<CenterPatientDetail>> =>
        apiClient.patch(`/centers/${centerId}/patients/${patientId}`, changes),

    /** Erase a patient or student the centre holds: the record and all of it, for good. */
    erasePatient: (centerId: string, patientId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/patients/${patientId}`),

    patients: (
        centerId: string,
        params: {
            therapist_id?: string;
            search?: string;
            course_id?: string;
            academic_year?: number;
            group_id?: string;
            couple_id?: string;
            offset?: number;
            limit?: number;
        } = {}
    ): Promise<AxiosResponse<CenterPatientPage>> =>
        apiClient.get(`/centers/${centerId}/patients`, { params }),

    reassign: (
        centerId: string,
        patientId: string,
        payload: { to_therapist_id: string; reason?: string }
    ): Promise<AxiosResponse<PatientReassignment>> =>
        apiClient.post(`/centers/${centerId}/patients/${patientId}/reassign`, payload),

    /** The caseload's couples and groups, all therapists' or one's. */
    units: (
        centerId: string,
        params: { therapist_id?: string } = {}
    ): Promise<AxiosResponse<CenterUnits>> =>
        apiClient.get(`/centers/${centerId}/units`, { params }),

    /** A couple or a group handed over whole: every member moves with it. */
    reassignUnit: (
        centerId: string,
        kind: "couple" | "group",
        unitId: string,
        payload: { to_therapist_id: string; reason?: string }
    ): Promise<AxiosResponse<PatientReassignment[]>> =>
        apiClient.post(`/centers/${centerId}/${kind === "couple" ? "couples" : "groups"}/${unitId}/reassign`, payload),

    /**
     * What handing somebody over would do to their bookings still to come:
     * how many move, which clash with the new therapist's, which are on a
     * Zoom or Meet link left in the previous therapist's account. Warnings.
     */
    reassignmentPreview: (
        centerId: string,
        input: ReassignmentPreviewInput
    ): Promise<AxiosResponse<ReassignmentPreview>> =>
        apiClient.post(`/centers/${centerId}/reassignment-preview`, {
            kind: input.kind,
            id: input.id,
            to_therapist_id: input.toTherapistId,
        }),

    reassignments: (
        centerId: string,
        patientId?: string
    ): Promise<AxiosResponse<PatientReassignment[]>> =>
        apiClient.get(`/centers/${centerId}/reassignments`, {
            params: patientId ? { patient_id: patientId } : undefined,
        }),

    agenda: (
        centerId: string,
        params: {
            start: string;
            end: string;
            therapist_id?: string[];
            session_status?: string;
            payment_status?: string;
            scheduled_in_alchely?: string;
            /** Repeated: every record one person holds in the centre. */
            patient_id?: string[];
            offset?: number;
            limit?: number;
        }
    ): Promise<AxiosResponse<CenterAppointmentPage>> =>
        apiClient.get(`/centers/${centerId}/agenda`, { params }),

    moveAppointment: (
        centerId: string,
        appointmentId: string,
        payload: { to_therapist_id: string }
    ): Promise<AxiosResponse<CenterAppointment>> =>
        apiClient.post(`/centers/${centerId}/appointments/${appointmentId}/move`, payload),

    sessionReport: (
        centerId: string,
        params: { start: string; end: string; therapist_id?: string }
    ): Promise<AxiosResponse<TherapistSessionReport[]>> =>
        apiClient.get(`/centers/${centerId}/reports/sessions`, { params }),

    agendaReport: (
        centerId: string,
        params: { start: string; end: string; therapist_id?: string }
    ): Promise<AxiosResponse<TherapistAgendaReport[]>> =>
        apiClient.get(`/centers/${centerId}/reports/agenda`, { params }),

    /** Sessions and people per day, month or year. Empty periods come as zero. */
    activityReport: (
        centerId: string,
        params: {
            start: string;
            end: string;
            granularity: ReportGranularity;
            therapist_id?: string;
        }
    ): Promise<AxiosResponse<ActivityPeriod[]>> =>
        apiClient.get(`/centers/${centerId}/reports/activity`, { params }),

    /** Paid and pending money per period, and a total per status. */
    paymentReport: (
        centerId: string,
        params: {
            start: string;
            end: string;
            granularity: ReportGranularity;
            therapist_id?: string;
        }
    ): Promise<AxiosResponse<CenterPaymentReport>> =>
        apiClient.get(`/centers/${centerId}/reports/payments`, { params }),

    paymentSettings: (centerId: string): Promise<AxiosResponse<CenterPaymentSettings>> =>
        apiClient.get(`/centers/${centerId}/payment-settings`),

    updatePaymentSettings: (
        centerId: string,
        payload: CenterPaymentSettingsInput
    ): Promise<AxiosResponse<CenterPaymentSettings>> =>
        apiClient.patch(`/centers/${centerId}/payment-settings`, payload),

    /** The consent URL for linking the centre's own Stripe account. */
    stripeAuthorizeUrl: (centerId: string): Promise<AxiosResponse<{ url: string }>> =>
        apiClient.get(`/centers/${centerId}/stripe/authorize`),

    disconnectStripe: (centerId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/stripe`),

    payments: (
        centerId: string,
        params: {
            therapist_id?: string;
            payment_status?: string;
            offset?: number;
            limit?: number;
        } = {}
    ): Promise<AxiosResponse<CenterPaymentPage>> =>
        apiClient.get(`/centers/${centerId}/payments`, { params }),

    toolPolicy: (centerId: string): Promise<AxiosResponse<CenterToolPolicy[]>> =>
        apiClient.get(`/centers/${centerId}/tool-policy`),

    setToolPolicy: (
        centerId: string,
        toolId: string,
        state: ToolPolicyState
    ): Promise<AxiosResponse<CenterToolPolicy>> =>
        apiClient.put(`/centers/${centerId}/tool-policy/${toolId}`, { state }),

    /** The caller's own patients the centre does not hold yet. */
    pendingConsent: (): Promise<AxiosResponse<CenterPatient[]>> =>
        apiClient.get("/centers/me/pending-consent"),

    /** One patient in — or, `withGroup`, everybody their groups need to stay whole. */
    recordConsent: (
        patientId: string,
        withGroup = false
    ): Promise<AxiosResponse<CenterConsentResult>> =>
        apiClient.post(
            `/centers/me/patients/${patientId}/consent`,
            undefined,
            withGroup ? { params: { with_group: true } } : undefined
        ),
};

/** A training centre's courses. Any member reads them; administrators change them. */
export const CenterCourseAPI = {
    list: (centerId: string): Promise<AxiosResponse<CenterCourse[]>> =>
        apiClient.get(`/centers/${centerId}/courses`),

    create: (centerId: string, course: CourseInput): Promise<AxiosResponse<CenterCourse>> =>
        apiClient.post(`/centers/${centerId}/courses`, course),

    update: (
        centerId: string,
        courseId: string,
        changes: CourseInput
    ): Promise<AxiosResponse<CenterCourse>> =>
        apiClient.patch(`/centers/${centerId}/courses/${courseId}`, changes),

    remove: (centerId: string, courseId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/courses/${courseId}`),

    /** Many courses at once, each with its plan; answered per course. */
    createMany: (
        centerId: string,
        courses: CourseInput[]
    ): Promise<AxiosResponse<{ results: CourseBatchResult[] }>> =>
        apiClient.post(`/centers/${centerId}/courses/batch`, { courses }),
};

/** A training centre's administrator takes students on: one, a group, or a file. */
export const CenterStudentAPI = {
    enrollMany: (
        centerId: string,
        students: StudentInput[]
    ): Promise<AxiosResponse<{ results: StudentBatchResult[] }>> =>
        apiClient.post(`/centers/${centerId}/students/batch`, { students }),
};

/**
 * A centre's own fields for its patients. Any member reads the definitions;
 * administrators change them and the values of anybody the centre holds.
 */
export const CenterPatientFieldAPI = {
    list: (centerId: string): Promise<AxiosResponse<{ fields: PatientCustomField[] }>> =>
        apiClient.get(`/centers/${centerId}/patient-fields`),

    create: (centerId: string, field: PatientCustomFieldInput): Promise<AxiosResponse<PatientCustomField>> =>
        apiClient.post(`/centers/${centerId}/patient-fields`, field),

    update: (
        centerId: string,
        fieldId: string,
        changes: PatientCustomFieldUpdate
    ): Promise<AxiosResponse<PatientCustomField>> =>
        apiClient.patch(`/centers/${centerId}/patient-fields/${fieldId}`, changes),

    /** Deleting a field deletes every value of it too. */
    remove: (centerId: string, fieldId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/centers/${centerId}/patient-fields/${fieldId}`),

    /** Every field's id, in the new order. */
    reorder: (centerId: string, fieldIds: string[]): Promise<AxiosResponse<{ fields: PatientCustomField[] }>> =>
        apiClient.put(`/centers/${centerId}/patient-fields/order`, { field_ids: fieldIds }),

    /** One held patient's values, as the centre's administrator reads them. */
    values: (centerId: string, patientId: string): Promise<AxiosResponse<PatientCustomValues>> =>
        apiClient.get(`/centers/${centerId}/patients/${patientId}/custom-fields`),

    saveValues: (
        centerId: string,
        patientId: string,
        values: PatientCustomValuesInput
    ): Promise<AxiosResponse<PatientCustomValues>> =>
        apiClient.put(`/centers/${centerId}/patients/${patientId}/custom-fields`, { values }),
};

/**
 * The same values, for the professional who owns the patient. A patient the
 * caller's centre does not hold answers with no fields at all.
 */
export const PatientCustomValuesAPI = {
    get: (patientId: string): Promise<AxiosResponse<PatientCustomValues>> =>
        apiClient.get(`/patients/${patientId}/custom-fields`),

    save: (patientId: string, values: PatientCustomValuesInput): Promise<AxiosResponse<PatientCustomValues>> =>
        apiClient.put(`/patients/${patientId}/custom-fields`, { values }),
};
